import type { LensDocument } from '~/pages/LeLens/document/types';

/**
 * One runtime list, so nothing can support a type the planner is not offered or
 * the parser silently degrades. The union is derived from it.
 */
export const LENS_CARD_TYPES = [
  'metric',
  'line',
  'bar',
  'drivers',
  'stacked',
  'area',
  'scatter',
  'pie',
  'combo',
  'heatmap',
  'boxplot',
  'table',
  'insight',
] as const;

export type LensCardType = (typeof LENS_CARD_TYPES)[number];

/** Labels shared by every UI that lets a user select a Lens card type. */
export const LENS_CARD_TYPE_OPTIONS: Array<{ value: LensCardType; label: string }> = [
  { value: 'metric', label: 'Metric' },
  { value: 'line', label: 'Line chart' },
  { value: 'bar', label: 'Bar chart' },
  { value: 'drivers', label: 'Drivers chart' },
  { value: 'stacked', label: 'Stacked bar chart' },
  { value: 'area', label: 'Area chart' },
  { value: 'scatter', label: 'Scatter plot' },
  { value: 'pie', label: 'Pie chart' },
  { value: 'combo', label: 'Combination (two scales)' },
  { value: 'heatmap', label: 'Heatmap' },
  { value: 'boxplot', label: 'Box plot' },
  { value: 'table', label: 'Table' },
  { value: 'insight', label: 'Text insight' },
];
export type LensFilter = {
  id: string;
  label: string;
  type: 'select' | 'dateRange' | 'boolean' | 'numberRange';
  column?: string;
  options?: Array<string>;
  multiple?: boolean;
  defaultValue?: string | Array<string> | boolean | { min?: number; max?: number } | { from: string; to: string };
};

/** The saved initial value for the dashboard-wide Period control. */
export type LensPeriodDefault =
  | { type: 'lastDays'; days: number }
  | { type: 'yearToDate' }
  | { type: 'monthToDate' }
  | { type: 'fixed'; from: string; to: string };

export type LensPeriodUnit = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year' | 'fiscalYear';
export type LensPeriodAnchor = 'yesterday' | 'today' | 'now' | 'lastHour' | { date: string } | { at: string };
export type LensPeriodWindow =
  | { days: number }
  | { hours: number }
  | { toDate: Exclude<LensPeriodUnit, 'hour' | 'day'> }
  | { calendar: LensPeriodUnit; back?: number; count?: number }
  | { from: string; to: string }
  | 'all';
export type LensPeriodComparison = 'previous' | { back: number; unit: LensPeriodUnit } | { from: string; to: string };
/** A window and its comparison as named inputs: the board's, or a card's own pin. */
export type LensPeriod = {
  anchor?: LensPeriodAnchor;
  window: LensPeriodWindow;
  comparison?: LensPeriodComparison | null;
};
export type LensNamedPeriod = LensPeriod & { id: string; label?: string };
/** A card's own pin, a named list of them, or "none" for a card that reads its dataset with no window. */
export type LensCardPeriod = LensPeriod | Array<LensNamedPeriod> | 'none';
/** The one zone, week start and fiscal year start every window on a board is decided in. */
export type LensCalendar = { zone: string; weekStart: 'monday' | 'sunday'; fiscalYearStart: number };
/** Inclusive civil days for anything that prints dates, half-open instants for anything that binds them. */
export type LensPeriodRange = { from: string; to: string; start: string; end: string };
/** What the server resolved a period to for this read, with the one set of labels everything prints. */
export type LensResolvedPeriod = {
  anchor: string;
  zone: string;
  partial: boolean;
  current: LensPeriodRange;
  comparison?: LensPeriodRange;
  label: { current: string; comparison?: string; note?: string };
  /** False when the comparison is not like for like, so no delta is shown. */
  comparable?: boolean;
};

/** Saved reader preferences, independent of how the Lens is presented. */
export const LENS_COLOR_TREATMENTS = ['plain', 'header', 'sections', 'metrics'] as const;
export type LensColorTreatment = (typeof LENS_COLOR_TREATMENTS)[number];

export type LensDisplay = {
  compactLayout?: boolean;
  showTitle?: boolean;
  /** Approved colour placement for the native Lens canvas. */
  colorTreatment?: LensColorTreatment;
};

/** Saved presentation source. Live numbers are supplied by the Lens data bridge. */
export type LensCustomView = {
  version: 1;
  html: string;
  css?: string;
  javascript?: string;
  height?: number;
  /** Keep the reader toolbar compact. Defaults to true for existing custom views. */
  compactLayout?: boolean;
  /** Show the Lens title above the custom view. Defaults to false. */
  showTitle?: boolean;
};

/** Every Lens date-range query uses these native BigQuery parameter names. */
export const LENS_DATE_START_PARAMETER = 'start_date' as const;
export const LENS_DATE_END_PARAMETER = 'end_date' as const;

export type LensFilterBinding =
  | { type: 'ignore'; column?: never }
  | { type: 'boolean' | 'numberRange'; column: string }
  | { type: 'select'; parameter?: string; column?: string }
  | {
      type: 'dateRange';
      startParameter?: typeof LENS_DATE_START_PARAMETER;
      endExclusiveParameter?: typeof LENS_DATE_END_PARAMETER;
      column?: string;
    };
export type LensFilterValues = Record<
  string,
  string | Array<string> | boolean | { min?: number; max?: number } | { from?: string; to?: string } | LensPeriod
>;

/** The viewer's own period, sent beside the declared filter values as the grammar the server resolves. */
export const LENS_PERIOD_KEY = 'period';

export type LensValueFormatKind = 'number' | 'currency' | 'percent';

/** How a card renders its numbers. Stored on the spec, not in the renderer. */
export type LensValueFormat = {
  kind: LensValueFormatKind;
  /** ISO 4217, required when kind is 'currency'. */
  currency?: string;
  /**
   * What a percent value already means. SQL that computes a percentage usually
   * multiplies by 100 already, so 17.08 means 17.08% and must not be
   * multiplied again; 'unit' is for a value still expressed as 0 to 1.
   */
  percentScale?: 'percent' | 'unit';
  decimals?: number;
  /** BCP 47 locale. Defaults to en-AU at render time. */
  locale?: string;
  prefix?: string;
  suffix?: string;
};

export type LensCardSize = 'small' | 'medium' | 'large' | 'full';

export type LensCardHeight = 'short' | 'standard' | 'tall' | 'xtall';

/** A card's saved location in the 12-column Lens canvas. Coordinates are
 * semantic grid frames, never viewport pixels, so the canvas can scale down
 * responsively without losing the editor's placement. */
export type LensCardLayout = { x: number; y: number; w: number; h: number; by?: 'user' | 'model' | 'auto' };

export type LensLabelKind = 'text' | 'date' | 'time';

/** Quick picks in the editor. Any pattern built from the tokens is accepted. */
export const LENS_DATE_PATTERN_PRESETS = [
  'dd/MM/yyyy',
  'dd/MM/yy',
  'd MMM yyyy',
  'd MMM',
  'MMM yyyy',
  'MMMM yyyy',
  'MM/yyyy',
  'yyyy-MM-dd',
] as const;

export const LENS_DATE_TOKENS = ['yyyy', 'yy', 'MMMM', 'MMM', 'MM', 'M', 'dd', 'd'] as const;

/** Tokens and separators only: free text would be printed back verbatim. */
export const LENS_DATE_PATTERN_RULE = /^(?:yyyy|yy|MMMM|MMM|MM|M|dd|d|[\s/\-.,:])+$/;

export function isValidDatePattern(pattern: string): boolean {
  return LENS_DATE_PATTERN_RULE.test(pattern) && /[yMd]/.test(pattern);
}

export const LENS_TIME_PATTERN_PRESETS = ['HH:mm', 'H:mm', 'HH:mm:ss'] as const;

export const LENS_TIME_TOKENS = ['HH', 'H', 'mm', 'm', 'ss', 's'] as const;

export const LENS_TIME_PATTERN_RULE = /^(?:HH|H|mm|m|ss|s|[\s/\-.,:])+$/;

export function isValidTimePattern(pattern: string): boolean {
  return LENS_TIME_PATTERN_RULE.test(pattern) && /[Hms]/.test(pattern);
}

export type LensDatePattern = string;

/** How the category axis and its tooltip label are rendered. */
export type LensLabelFormat = {
  kind: LensLabelKind;
  pattern?: LensDatePattern;
};

/** Presentation controls for dense categorical x axes. */
export type LensXAxisConfig = {
  /** Let Recharts thin labels, or render every returned category. */
  tickDensity?: 'auto' | 'all';
  /** Clockwise angle applied to each x-axis label. */
  tickRotation?: 0 | 30 | 45 | 60 | 90;
  /** Reader-facing axis title. Omitted draws none. */
  title?: string;
};

/** Controls for a value axis on the shared axis charts: bar, stacked, line, area, combo. */
export type LensValueAxisConfig = {
  /** Reader-facing axis title. Omitted draws none. */
  title?: string;
  /**
   * Lower bound. Omitted starts the axis at zero, which is the default for
   * every card. Set aside at render time when any plotted value sits below it,
   * because a mark below the floor is not drawn short, it is not drawn at all,
   * and a missing bar reads as zero. Never applied to a stack, where cutting
   * the bottom segment misstates every proportion above it.
   */
  min?: number;
  /** Upper bound. Omitted lets the nice-bound ladder choose. */
  max?: number;
};

/** Whether each mark prints its own value, and for which series. */
export type LensValueLabelsConfig = {
  show: boolean;
  /** Which marks receive a label. Omitted preserves the original per-mark behaviour. */
  mode?: 'values' | 'totals' | 'top' | 'last';
  /** Label-only text scale. Omitted follows the chart's shared text scale. */
  size?: LensTextScale;
  /** Series that carry labels. Omitted labels every series. */
  series?: Array<string>;
};

/** Whether a chart's computed description is available beneath its plot. */
export type LensCommentaryConfig = {
  /** Omitted is visible, so existing cards keep commentary by default. */
  show?: boolean;
  /** Legacy placement accepted for saved specs; chart descriptions now render below. */
  position?: 'right' | 'below';
};

/** A deliberate scale for axis, legend and value-label text, rather than a pixel input. */
export type LensTextScale = 'small' | 'medium' | 'large';

/** A horizontal guide at a fixed value: a target, a budget, a prior-year average. */
export type LensReferenceLine = {
  value: number;
  label?: string;
  /** Which scale the value is read on. Omitted means the primary axis. */
  axis?: 'primary' | 'secondary';
};

export type LensSortKey = 'none' | 'label' | 'value';

export type LensSeriesSort = 'none' | 'largest' | 'smallest' | 'alpha';

/** An explicit placement for a card title. Omitted preserves the card type's usual layout. */
export type LensCardAlignment = 'left' | 'center' | 'right';

/** A deliberate title scale, rather than an unconstrained font-size input. */
export type LensCardTitleSize = 'small' | 'medium' | 'large';

/**
 * Row order applied after the query returns. Instant and free, but it can only
 * reorder rows the query returned: a query with its own LIMIT needs its own
 * ORDER BY.
 */
export type LensSortConfig = {
  by: LensSortKey;
  direction?: 'asc' | 'desc';
  /** With by='value', sort on this series or column instead of the row total. */
  series?: string;
};

/** Ordered projections of a native table's current and comparison results. */
export type LensTableColumn = {
  kind: 'group' | 'share' | 'current' | 'prior' | 'change' | 'change_pct';
  field: string;
};

/** Display options that do not change what the query returns. */
export type LensCardConfig = {
  /** Ordered columns, including comparison reductions, for a configured native table. */
  tableColumns?: Array<LensTableColumn>;
  /** Optional placement for the title and subtitle. */
  titleAlignment?: LensCardAlignment;
  /** Optional title scale. */
  titleSize?: LensCardTitleSize;
  /** Default format for every numeric field on the card. */
  format?: LensValueFormat;
  /** Per-field overrides, keyed by column or series name. */
  fieldFormats?: Record<string, LensValueFormat>;
  /** Reader-facing names for query fields. Keys always remain the raw query columns. */
  fieldLabels?: Record<string, string>;
  /** Display names for dimension values. Query and selection values stay unchanged. */
  fieldValueLabels?: Record<string, Record<string, string>>;
  /** Row/category order. Omitted means the order the query returned. */
  sort?: LensSortConfig;
  /** An order set by hand on the chart. Takes precedence over sort. */
  categoryOrder?: Array<string>;
  /** Stacking and legend order for multi-series cards. */
  seriesSort?: LensSeriesSort;
  /** Rendering for the x axis labels, and the matching table column. */
  labelFormat?: LensLabelFormat;
  /** Label density, angle and title for charts with an x axis. */
  xAxis?: LensXAxisConfig;
  /** The primary value axis. */
  yAxis?: LensValueAxisConfig;
  /** The second scale on a combo. Applies to the series named in secondaryAxis. */
  secondaryYAxis?: LensValueAxisConfig;
  /** Value printed beside each mark. */
  valueLabels?: LensValueLabelsConfig;
  /** Display of the short computed reading attached to a chart. */
  commentary?: LensCommentaryConfig;
  /** Text scale for axis labels, axis titles, legend and value labels. */
  textScale?: LensTextScale;
  /** Horizontal guides at fixed values, drawn as markLines on the axis they name. */
  referenceLines?: Array<LensReferenceLine>;
  /** Mark per series, for a card mixing them. */
  seriesMarks?: Record<string, LensMark>;
  /** Series plotted against a second scale. */
  secondaryAxis?: Array<string>;
  /** Series colours in plot order, cycled when there are more series. */
  colors?: Array<string>;
  /**
   * The one series drawn in the brand's highlight colour (Coral Green) with
   * the Lens ring-dot marker, instead of its ordinary palette slot. An active
   * cross-filter selection on the series column has the same effect without
   * this being set, so this is only needed to call one series out by default.
   */
  highlightSeries?: string;
  /** Stack series instead of grouping them. Implied by the 'stacked' type. */
  stacked?: boolean;
  /** Bar direction. Omitted preserves the vertical default. */
  barOrientation?: 'vertical' | 'horizontal';
  /** Interpolation between points on line charts. Omitted keeps the straight-segment default. */
  lineStyle?: 'smooth' | 'straight';
  /** Column for the x axis. Inferred from the result when omitted. */
  xColumn?: string;
  /** Column for the y axis on a card with two category axes. */
  yColumn?: string;
  /** Explicit y columns for a wide result set. Ignored when categoryColumn is set. */
  series?: Array<string>;
  /** Column holding series names in a long result set, pivoted at render time. */
  categoryColumn?: string;
  /** Column holding the value when categoryColumn is set. */
  valueColumn?: string;
  /**
   * A deliberately compact rendering for a single-row comparison result. The
   * query and fields remain a table, but its measures read as a comparison at
   * a glance rather than as a sparse one-row grid.
   */
  summary?: 'stats';
  /** A metric card's trend line beside (or, centred, under) the figure. Defaults to true when a trend exists. */
  sparkline?: boolean;
  [key: string]: unknown;
};

/**
 * Governance state shown as a chip on every card. A card carrying none of
 * these is not blank: it renders a neutral "uncertified" chip, because a card
 * with no chip at all reads as certified by omission.
 */
export type LensContract = (typeof LENS_CONTRACTS)[number];

/**
 * The durable result of an AskRichard insight. The question remains separate
 * so a refresh can answer the same request again without treating the last
 * answer as the source of truth.
 */
export type LensInsight = {
  content: string;
  refreshedAt: string;
  /** The resolved viewer scope that supplied this saved analysis. Older authored notes omit it. */
  scope?: LensInsightScope;
};

export type LensInsightScope = {
  effectiveFilters: LensFilterValues;
  ranges?: Record<string, { min?: number; max?: number }>;
};

/**
 * How a card or section is read once it has numbers on it.
 *
 * A reading is an attribute of a card or a section, rendered in its chrome. It
 * is never a card of its own: commentary that competes with charts for a grid
 * slot is what the insight card already does, and it is the reason a board
 * either has no commentary or loses a chart to get some.
 *
 * 'computed' is arithmetic over the rows the card is already drawing, so it
 * costs nothing, cannot disagree with the chart above it, and recalculates the
 * instant a cross-filter moves. 'narrative' is written by a model and costs a
 * call, so a plan grants it only where reading across several cards says
 * something no single chart can.
 */
export const LENS_COMPUTED_TEMPLATES = ['delta', 'trajectory', 'contribution', 'concentration', 'spread'] as const;

export type LensComputedTemplate = (typeof LENS_COMPUTED_TEMPLATES)[number];

/**
 * Saved narrative text, together with the view state that produced it.
 *
 * `viewSignature` is what stops a saved paragraph describing numbers it never
 * saw. Text is only shown while the signature still matches the filters,
 * selections, comparison and Lens version in front of the reader.
 */
export type LensReadingCache = {
  content: string;
  refreshedAt: string;
  viewSignature: string;
};

export type LensReading =
  | { mode: 'computed'; template?: LensComputedTemplate }
  | { mode: 'narrative'; question: string; cached?: LensReadingCache };

export type LensCardBrief = {
  request: string;
  /** Fully-qualified table the author named for this card, when one was named. */
  source?: string;
  /** Constraints that apply to this card: cohort, denominators, forbidden columns. */
  notes?: string;
};

export type LensCardRefusal = {
  at: string;
  reason: string;
};

export type LensCard = {
  id: string;
  type: LensCardType;
  title: string;
  description?: string;
  /** Question AskRichard answers in a text insight card. */
  insightQuestion?: string;
  /** Last saved AskRichard answer for an insight card. Never regenerated on read. */
  insight?: LensInsight;
  filterBindings?: Record<string, LensFilterBinding>;
  config?: LensCardConfig;
  /** Grid width. Defaults to the two-per-row size the first cards used. */
  size?: LensCardSize;
  /** Plot height, independent of width. */
  height?: LensCardHeight;
  /** Explicit canvas frame. Absent cards are placed by the smart layout engine. */
  layout?: LensCardLayout;
  /** Id of the section this card belongs to. */
  section?: string;
  /** Pins the card to its own window, or several named ones, instead of the board's. */
  period?: LensCardPeriod;
  /** How this card is read. Absent falls back to the card type's own template. */
  reading?: LensReading;
  /** How this card reduces its rows. Required to read a shared dataset well. */
  view?: LensCardView;
  /** Id of a shared dataset. */
  datasetId?: string;
  /** Certification state for the card's footer chip. Absent renders "uncertified". */
  contract?: LensContract;
  /** The author's own words for a card not yet bound to data; a pending card renders these. */
  brief?: LensCardBrief;
  /** What the author could not resolve; answered by whoever binds the card. */
  questions?: Array<string>;
  /** Why the last attempt to bind or run this card was refused. */
  refusal?: LensCardRefusal;
};

export type LensAggregate = 'sum' | 'avg' | 'median' | 'count' | 'countDistinct' | 'min' | 'max' | 'first';

/**
 * Which cut of a dataset a card shows. A shared dataset returns one row set;
 * each card says how to reduce it to the shape it draws, so a metric and a
 * stacked chart can sit on the same query.
 */
export type LensViewMode = 'grouped' | 'points';

export type LensMark = 'line' | 'bar' | 'area';

export type LensCardView = {
  /** An explicitly cross-window SQL result needs no second comparison reduction. */
  comparison?: 'embedded';
  /**
   * 'grouped' combines rows sharing an x; 'points' keeps one mark per row, for
   * forms plotting a row against two measures rather than summarising it.
   */
  mode?: LensViewMode;
  x?: string;
  series?: string;
  value?: string;
  /** Further measures plotted alongside the first. */
  values?: Array<string>;
  aggregate?: LensAggregate;
  /**
   * A measure that is a ratio of two other columns, computed as
   * sum(of) / sum(per) for each group.
   *
   * A rate cannot be stored as a column on a dataset that a coarser card will
   * roll up: summing per-category rates is meaningless and averaging them
   * weights every category equally. Deriving the rate at the point of reading
   * is what lets one dataset at the finest grain serve both the rate by
   * category and the rate overall.
   */
  ratio?: { of: string; per: string };
  limit?: number;
  /**
   * Row filters applied before any grouping, so a card can read a narrower
   * cut of a shared dataset instead of forcing a second, near-identical
   * dataset into existence. Each entry names one dimension or date column
   * and keeps or excludes rows by value; a chat answer scoped to one country
   * or product is this, not a fresh query.
   */
  where?: Array<{ column: string; in?: Array<string>; notIn?: Array<string> }>;
};

/**
 * A named query several cards can share. Held once so it is run once per
 * dashboard load rather than once per card.
 */
/**
 * How a dataset's rows are bounded in time.  Recorded rather than left implicit
 * in the SQL, so the reader can re-point the range at another date.
 */
export type LensDatasetDateFilter = {
  /** Column the range currently filters on. */
  column: string;
  /**
   * True when the query carries @start_date and @end_date itself, which is the
   * form BigQuery prunes partitions for.  False means the range is applied by
   * wrapping the query: correct, but it reads everything first.
   */
  pruned: boolean;
  /** Other date columns the query exposes, offered when re-pointing. */
  available?: Array<string>;
};

/**
 * A column computed from a dataset's own columns rather than returned by its
 * query.
 *
 * `when` is the load-bearing part, and getting it wrong produces a plausible
 * wrong number rather than an error:
 *
 * - 'row' evaluates once per returned row, before any grouping. The result is
 *   an ordinary column: groupable, filterable, summable. Use it for anything
 *   that is a property of a row, such as `if(product = 'hotel', ttv_aud, 0)`.
 * - 'aggregate' evaluates once per group, after grouping, so a column
 *   reference means that column's total for the group. Use it for every rate,
 *   share, average and per-unit value: `gross_margin_aud / ttv_aud` here is
 *   total margin over total TTV, which is correct at any grain, whereas the
 *   same formula as a 'row' field averages per-row rates and weights a booking
 *   of $100 the same as one of $30,000.
 */
export type LensDatasetField = {
  name: string;
  /** In the small formula language: arithmetic, comparisons, and if/coalesce. */
  expression: string;
  when: 'row' | 'aggregate';
  /** How to print it, since a computed rate has no column type to infer from. */
  format?: LensValueFormat;
  /** Reader-facing label, when the name alone reads as a column name. */
  label?: string;
};

/**
 * What a declared column is for. Populated from a BigQuery dry run, never
 * guessed from its name: DATE, DATETIME and TIMESTAMP are 'date'; a numeric
 * type is 'measure' unless its name ends in _id, _bk or _key; everything else
 * is 'dimension'. 'calculated' is reserved for a LensDatasetField promoted
 * onto this list, never inferred.
 */
export const LENS_COLUMN_KINDS = ['date', 'dimension', 'measure', 'calculated'] as const;

export type LensColumnKind = (typeof LENS_COLUMN_KINDS)[number];

/**
 * One column a dataset's query is known to return, typed from a BigQuery dry
 * run rather than read out of the SQL text or a sample result. This is the
 * schema every card's view resolves against in a version 2 spec.
 */
export type LensDatasetColumn = {
  name: string;
  /** The BigQuery type string the dry run reported, e.g. STRING, INT64, DATE. */
  type: string;
  kind: LensColumnKind;
  /** Reader-facing label, when the column name alone reads as internal. */
  label?: string;
  format?: LensValueFormat;
  /** Plain-English meaning, from dbt docs on a governed dataset. */
  description?: string;
  /** How a governed measure is meant to combine: the Combine control's default and, for countDistinct, its only honest choice. */
  aggregate?: LensAggregate;
  /** A governed ratio measure: picking it onto a shelf sets view.ratio rather than view.value. */
  ratio?: { of: string; per: string };
  /** DATETIME columns only: the zone its wall clock is written in. */
  zone?: string;
};

/**
 * The columns that uniquely identify a row, declared by the dataset's author
 * and checked for uniqueness against the rows a run actually returns rather
 * than parsed out of a GROUP BY, which fails on a CTE. `verified` is set
 * after that check, not by the author: a dataset can be saved with keys that
 * turn out not to be unique, and the panel says so rather than refusing.
 */
export type LensDatasetGrain = {
  keys: Array<string>;
  verified?: boolean;
};

export const LENS_DATASET_SOURCE_KINDS = ['sql', 'catalogue', 'governed'] as const;

export type LensDatasetSourceKind = (typeof LENS_DATASET_SOURCE_KINDS)[number];

/**
 * Where a dataset's query came from. 'catalogue' is a certified table picked
 * from the static day-one list; 'sql' is anything typed or generated
 * free-hand, including a one-card dataset minted for a single card.
 */
export type LensDatasetSource = {
  /** Fully-qualified source table, when one is known. */
  table?: string;
  kind: LensDatasetSourceKind;
  /**
   * For a 'governed' dataset: the catalogue entry it was taken from. The
   * server compiles every card on this dataset into an aggregate query against
   * the catalogue's table using only the catalogue's declared fields, so the
   * dataset carries no SQL a person can edit.
   */
  catalogueId?: string;
  /** Catalogue version the columns were copied from, so a later crawl can say what changed. */
  version?: string;
  /** Who declared this SQL: the human actor and, for a delegated tool call, the client that carried it. */
  authoredBy?: LensDatasetAuthor;
};

export type LensDatasetAuthor = {
  actor: string;
  clientId?: string;
};

/**
 * Result of the last BigQuery dry run for this dataset's SQL. Recorded so the
 * panel can show the cost of the query that produced the current columns
 * without re-running it.
 */
export type LensDatasetDryRun = {
  /** ISO timestamp of the dry run this dataset's columns were read from. */
  at: string;
  bytesProcessed: number;
  /**
   * Fully-qualified (project.dataset.table) tables BigQuery's job statistics
   * reported the query touching. A fact read off the dry run rather than
   * parsed from SQL text, so a CTE, a view, or a wildcard table does not have
   * to be understood here to know what a dataset or a pinned query reads.
   */
  referencedTables: Array<string>;
};

/**
 * Records that a dashboard owner applied a grain-changing extension despite
 * the extend-check guard's refusal (decision D7: Apply anyway is available
 * to the dashboard owner only). A fact about what happened, not a
 * permission: the check that let the write through already ran, this is
 * only the record of it, kept on the dataset so a reader can see a guard
 * was overridden and by whom.
 */
export type LensDatasetOverride = {
  overriddenBy: string;
  /** ISO timestamp of the override, not of the extension it applied. */
  at: string;
};

export type LensDatasetScope = (typeof LENS_DATASET_SCOPES)[number];

export type LensDatasetRecord = {
  deletedAt?: string;
  version: number;
  scope: LensDatasetScope;
  /** The identity its reads are attributed to, which is not yet the identity they execute as. */
  runAs: string;
  /** Which warehouse serves it, so a read can move to a warm copy without the SQL changing. */
  residence: string;
  description?: string;
  /** Governance state of the data source itself; blocked withholds every card that reads it. */
  contract?: LensContract;
};

export type LensDataset = Partial<LensDatasetRecord> & {
  maxAgeSeconds?: number;
  id: string;
  name: string;
  sql: string;
  /** Columns computed from the ones the query returns. */
  fields?: Array<LensDatasetField>;
  /** Columns worth offering as a selection control: dimensions, not measures. */
  dimensions?: Array<string>;
  /** Reader-facing label per dimension column, when an editor named one explicitly. */
  dimensionLabels?: Record<string, string>;
  /** Time bound. Absent means the dashboard's period leaves this query alone. */
  dateFilter?: LensDatasetDateFilter | null;
  /**
   * The dataset's declared, typed schema, from a BigQuery dry run. Absent on
   * a dataset saved before this existed; the panel offers a "Declare now"
   * state rather than guessing one from SQL text.
   */
  columns?: Array<LensDatasetColumn>;
  /** Declared uniqueness keys and whether the last run's rows bore them out. */
  grain?: LensDatasetGrain;
  source?: LensDatasetSource;
  dryRun?: LensDatasetDryRun;
  /** Set when the dashboard owner applied a guard-refused extension anyway. */
  override?: LensDatasetOverride;
};

/**
 * The dashboard period arrives under a reserved filter id.  It belongs to the
 * dashboard rather than to any card's binding, and reserving an id means a Lens
 * built before date filtering existed gains the control without a migration.
 */
export const LENS_PERIOD_FILTER_ID = 'lens_period';

/** Window a dashboard loads with when nobody has chosen one. */
export const LENS_DEFAULT_DATE_WINDOW_DAYS = 90;

/** A named group of cards. Cards reference a section by id. */
export type LensSection = {
  id: string;
  title: string;
  /** The question this section answers, carried through from the plan. */
  question?: string;
  /** A narrative reading across this section's cards, where the mode grants one. */
  reading?: LensReading;
};

export type LensStatus = 'draft' | 'published';

export type LensSpec = {
  version: 1;
  customView?: LensCustomView;
  display?: LensDisplay;
  filters?: Array<LensFilter>;
  periodDefault?: LensPeriodDefault;
  period?: LensPeriod;
  calendar?: LensCalendar;
  cards: Array<LensCard>;
  sections?: Array<LensSection>;
  datasets?: Array<LensDataset>;
  filterColumns?: Array<string>;
  scope?: LensScope;
};

export type Lens = {
  /** Application page published into the Library by deployment. */
  nativePage?: { source: string; pageId: string; release: string } | null;
  id: string;
  title: string;
  description: string | null;
  spec: LensSpec | LensDocument;
  status: LensStatus;
  version: number;
  ownerEmail: string;
  /** The Lens's home. Null is Personal; a value is the team space it lives in. */
  spaceId?: string | null;
  area?: 'commercial' | 'growth' | 'loyalty' | 'supply' | 'customer-service' | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  /**
   * The four fields below are added by the list endpoint, which answers "where does
   * this live", "who can see it" and "what may I do with it" per row. They are
   * optional because the detail endpoint returns the definition alone, with access
   * and sharing beside it rather than on it.
   */
  home?: LensHome;
  audience?: LensAudience;
  access?: LensAccess;
  /** Who shared this Lens with the reader by name, when that is how they can see it. */
  sharedBy?: string | null;
  /** How many people hold a direct share. Only meaningful to a reader who may share. */
  recipientCount?: number;
  certification?: LensCertification;
  /** Distinct readers over the last seven days, the order every shelf ranks by. */
  readersThisWeek?: number;
};

export type CardLens = Lens & { spec: LensSpec };

export function isCardLens(lens: Lens): lens is CardLens {
  return 'cards' in lens.spec && Array.isArray(lens.spec.cards);
}

/** What the reader may do across the library, decided by the server. */
export type LensLibraryAccess = { canSeeEveryLens: boolean; canReviewCertification: boolean; canCreateFolder: boolean };

/**
 * What one person may do with one Lens, weakest to strongest. A reader who reaches
 * the same Lens by two routes -- a direct share and a team space -- keeps the
 * stronger of the two, which the service resolves before it answers.
 */
export const LENS_ROLES = ['viewer', 'editor', 'manager'] as const;
export type LensRole = (typeof LENS_ROLES)[number];

export const LENS_ROLE_LABELS: Record<LensRole, string> = {
  viewer: 'Can view',
  editor: 'Can edit',
  manager: 'Manager',
};

/** Where a Lens lives, which is a different question from who can see it. */
export type LensHome = { kind: 'personal'; ownerEmail: string } | { kind: 'space'; spaceId: string; spaceName: string };

/** Who can see a Lens. Widest route wins, and the service derives it rather than storing it. */
export const LENS_AUDIENCES = ['private', 'people', 'team', 'company', 'reporting-admins'] as const;
export type LensAudience = (typeof LENS_AUDIENCES)[number];

export type LensAccess = {
  role: LensRole | null;
  canRead: boolean;
  canPropose: boolean;
  canEdit: boolean;
  canReview: boolean;
  canRestore: boolean;
  canDelete?: boolean;
  canShare: boolean;
  canShareEdit?: boolean;
  canManageSharing?: boolean;
  canShareCompany?: boolean;
  canRequestCertification: boolean;
  canCertify: boolean;
  canRemoveCertification: boolean;
};

type LensCertificationStamp = { by: string; at: string };

export type LensCertification = {
  badge:
    | { kind: 'none' }
    | ({ kind: 'certified'; note: string | null } & LensCertificationStamp)
    | ({ kind: 'changed' } & LensCertificationStamp);
  request:
    | { kind: 'none' }
    | ({ kind: 'open'; note: string | null } & LensCertificationStamp)
    | ({ kind: 'declined'; note: string } & LensCertificationStamp);
  /** Identifies what the Lens measured when this was read; Certify sends it back. */
  meaning: string;
};

export type LensSpaceMember = {
  email: string;
  role: LensRole;
  addedBy?: string;
  addedAt?: string;
  createdAt?: string;
};

export type LensSpace = {
  id: string;
  name: string;
  description: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** The reader's own role in this space. Null when they are not a member. */
  role: LensRole | null;
  memberCount: number;
  lensCount: number;
  audience: 'members' | 'everyone';
  visibility?: 'open' | 'hidden';
  area?: 'commercial' | 'growth' | 'loyalty' | 'supply' | 'customer-service' | null;
  archivedAt?: string | null;
  isMember?: boolean;
  main?: boolean;
  pinOrder?: number | null;
  members?: Array<LensSpaceMember>;
};

export type LensShareRecipient = {
  email: string;
  role: LensRole;
  sharedBy?: string;
};

/** The detail endpoint's sharing block: this Lens's home, audience and named recipients. */
export type LensSharing = {
  home: LensHome;
  audience: LensAudience;
  /** Signed-in employees with the Lens URL can read, without edit or team membership. */
  linkAccess?: { enabled: boolean };
  /** Present only for a reader who may change it. */
  recipients?: Array<LensShareRecipient>;
  recipientCount: number;
};

export type LensGovernedMetadata = {
  datasets: Array<{ id: string; columns: Array<{ name: string; label?: string; description?: string }> }>;
};

/** Authoritative read-only measure semantics; never part of a saved Lens definition. */
export type LensGovernedMeasureMetadata = {
  datasets: Array<{
    id: string;
    columns: Array<Pick<LensDatasetColumn, 'name' | 'ratio' | 'aggregate' | 'format'>>;
  }>;
};

export type LensResponse = {
  lens: Lens;
  /** Read-only friendly metadata; never merge into saved/editor definitions. */
  governedMetadata?: LensGovernedMetadata;
  governedMeasureMetadata?: LensGovernedMeasureMetadata;
  drafting?: boolean;
  access: {
    canEdit: boolean;
    canRead?: boolean;
    canPropose?: boolean;
    canReview?: boolean;
    canRestore?: boolean;
    canRequestCertification?: boolean;
    canCertify?: boolean;
    canRemoveCertification?: boolean;
    canShare?: boolean;
    canShareEdit?: boolean;
    canManageSharing?: boolean;
    canShareCompany?: boolean;
    role?: LensRole | null;
  };
  sharing?: LensSharing;
  view?: LensView;
  certification?: LensCertification;
};

export type LensViewChange = {
  id: number;
  changed: {
    cards: Array<{ id: string; title: string; change: 'added' | 'removed' | 'updated'; fields: Array<string> }>;
    lens: Array<string>;
    datasets: Array<string>;
  };
  noLongerApplies: Array<string>;
};

export type LensView = { revision: number; changes: Array<LensViewChange>; suggestion: { id: string } | null };

/**
 * One card in the plan POST /api/reporting/le-lenses/plan returns. `request`
 * is a standalone sentence, resolvable with no memory of the rest of the
 * plan, matching the same convention the plan gate's own plan cards
 * use. `suggestedDatasetId` is present only when the model named a dataset
 * from the catalogue the request carried; it is a hint for the resolve step,
 * never a binding taken on trust.
 */
export type LensPlanCard = {
  /** Stable id within one plan (server `plan.cards[].id`); the key every redesign surface uses
   *  for selection, Changed marks and per-card build progress. */
  id?: string;
  title: string;
  type: LensCardType;
  request: string;
  suggestedDatasetId?: string;
  /** Set when the plan was anchored on a governed dataset: the card is already bound and needs no query generation. */
  datasetId?: string;
  view?: LensCardView;
  /** The governed dataset a routed plan bound this card to, so the client can seed it into the Lens. */
  catalogueId?: string;
  /** The id of the plan section this card belongs to. Absent from a service that predates modes. */
  section?: string;
  /** The analytical question this card answers, which is not the same as what it draws. */
  question?: string;
};

/**
 * What kind of analysis a Lens is. The plan endpoint infers it from the brief
 * and the reader can correct it at the review gate.
 */
export const LENS_PLAN_MODES = ['monitor', 'diagnose', 'explore'] as const;

export type LensPlanMode = (typeof LENS_PLAN_MODES)[number];

/** One group of planned cards, named by the question it answers rather than by the charts in it. */
export type LensPlanSection = {
  id: string;
  title: string;
  question: string;
};

/** The governed dataset a plan or a composed card must answer from. */
export type LensPlanAnchor = {
  /** The dataset's id inside the Lens spec. */
  datasetId: string;
  /** The catalogue entry whose fields the server may use. */
  catalogueId: string;
};

/** The Key Tracking Report basis a plan's TTV or margin cards read on. 'other' covers a metric
 *  the plan's cards do not carry a stated basis for at all. */
export type LensScopeMetricBasisId = 'ttv_br' | 'ttv_ar' | 'margin_br' | 'margin_ar' | 'other';

/** Which family of "region" field a plan's geography term resolved to: a travel destination, the
 *  storefront site the booking was made on, an LE staff owner's patch, or something else the
 *  catalogue does not group under those three. */
export type LensScopeGeographyFamily = 'destination' | 'website_region' | 'owner' | 'other';

/**
 * The planner's own reading of a brief's ambiguous terms, in the reader's words: which dataset,
 * which basis TTV or margin is read on, what period and comparison, what "region" or "market"
 * means here, and which attribution model (or none) applies. Every field is independently
 * nullable: a null means the planner has nothing to state for that term, which is not the same
 * as an unresolved `LensClarification` for it (a term can be null and still not ambiguous, e.g.
 * a brief with no geography words at all). Shared, byte-for-byte in shape, between the plan
 * response (`LensPlan.scope`) and the saved spec (`Lens['spec']['scope']`, which carries no
 * `clarifications`: nothing is saved until every one of those is resolved).
 */
export type LensScope = {
  dataset: { catalogueId?: string; name: string } | null;
  metricBasis: { id: LensScopeMetricBasisId; label: string } | null;
  period: { label: string; periodDefault?: LensPeriodDefault } | null;
  /** Null means "no comparison", stated on purpose rather than left absent. */
  comparison: { label: string } | null;
  geography: { field: string; family: LensScopeGeographyFamily; label: string } | null;
  attribution: { model: string; label: string } | { notApplicable: true } | null;
};

/** One candidate answer to a `LensClarification`, in the reader's words. Choosing it sends
 *  `resolution` back to `/plan` as a `LensScopeResolution`, which the server appends to the
 *  brief as a stated fact so the same term is not asked about again. */
export type LensClarificationOption = {
  id: string;
  label: string;
  description: string;
  /** Live example values for this meaning (e.g. the top destination regions by TTV), read from the
   *  option's own governed field, never a fixed list. Absent when the lookup failed. */
  examples?: Array<string>;
  /** The governed field this option binds to, so the client can say what it will filter on. */
  field?: string;
  /** Server-marked "this is the meaning most readers want" for this term. Drives the "Usual" tag
   *  in the clarification panel; absent (or an older service) shows no tag at all rather than
   *  guessing. This field was missing from the frontend contract even though svc-reporting's
   *  `detectScope` already computes it (confirmed against lane B1's report: "`recommended` is
   *  untouched, still purely `detectScope`'s output"); added here purely to catch the frontend
   *  type up to data already on the wire, for the Sep-26 create redesign (00_BRIEF.md ask 1). */
  recommended?: boolean;
  resolution: string;
  /** The governed catalogue entry this option's meaning binds to, when it names one directly
   *  (e.g. a market-manager/owner option pointing at a specific dataset). Matches B1's own
   *  addition (svc-reporting d3173676); no JSON contract change, since clarifications never
   *  survive onto a saved Lens (LensScope, the saved twin, has no field for them at all). */
  catalogueId?: string;
};

/**
 * One term in the brief the planner could not resolve on its own, with the candidates the
 * catalogue actually has for it. The scope confirmation block renders one of these in place of
 * its row as a question with its options; Build stays disabled while any remain unresolved.
 */
export type LensClarification = {
  /** Known ids from the deterministic detector (svc-reporting scope.ts): 'geography',
   *  'metric_basis', 'attribution', 'period', 'brand'. Kept as a plain string, not a closed
   *  union, since the detector can add a new family without a type change on this side. */
  id: string;
  /** The words in the brief that were ambiguous, e.g. "my region". */
  term: string;
  /** One plain-English sentence, e.g. "Which region do you mean?" */
  question: string;
  options: Array<LensClarificationOption>;
};

/** What the scope confirmation block sends back to `/plan` once a clarification is chosen. */
export type LensScopeResolution = { clarificationId: string; optionId: string; resolution: string };

export type LensPlan = {
  periodDefault?: LensPeriodDefault;
  title: string;
  cards: Array<LensPlanCard>;
  /** Governed datasets a routed plan wants in the Lens, with the local ids its cards use. */
  datasets?: Array<LensPlanAnchor>;
  /** What kind of analysis this is. Absent from a service that predates modes. */
  mode?: LensPlanMode;
  /** One sentence saying why that mode was chosen, shown at the review gate. */
  modeReason?: string;
  /** The sections the cards are grouped into, in the order they should be read. */
  sections?: Array<LensPlanSection>;
  /** The planner's interpreted scope: dataset, metric basis, period and comparison, geography
   *  meaning, attribution. Absent from a service that predates scope; a `starter` plan still
   *  carries one, with every field null and the deterministic detector's own clarifications. */
  scope?: LensScope;
  /** Terms the planner (model plus deterministic detector) could not resolve. Empty or absent
   *  means nothing is blocking Build. */
  clarifications?: Array<LensClarification>;
};

export type LensCardComparison = Pick<
  LensCardData,
  'status' | 'columns' | 'rows' | 'message' | 'aggregated' | 'truncated' | 'refreshedAt'
>;
export type LensCardPeriodResult = {
  id: string;
  label: string;
  period: LensResolvedPeriod;
  result: LensCardComparison;
  comparison?: LensCardComparison;
};

export type LensCardData = {
  /** The query has its own dates or no date binding; the dashboard period does not apply. */
  unbound?: boolean;
  /** The window this result answers for, and the labels to print for it. */
  period?: LensResolvedPeriod;
  /** The same card under the comparison window, when its period declares one. */
  comparison?: LensCardComparison;
  /** One entry per named period on a card that lists several; rows mirror the first. */
  periods?: Array<LensCardPeriodResult>;
  appliedFilterColumns?: Array<string>;
  appliedFilters?: Array<string>;
  unappliedFilters?: Array<string>;
  cardId: string;
  /** 'restricted' has no producer yet: the value exists so a future per-viewer
   *  permission check has a status to land on without a type change. */
  status: 'ready' | 'empty' | 'unconfigured' | 'error' | 'restricted';
  columns?: Array<string>;
  rows?: Array<Record<string, unknown>>;
  message?: string;
  missingParameters?: Array<string>;
  /** Where the card's numbers came from, for the footer. Already formatted by
   *  the backend, e.g. "reporting.sales", not a table id to look up. */
  source?: string;
  /** When the card's data was produced, for the footer. Already formatted,
   *  e.g. "06:15 today", not a timestamp the client parses. */
  refreshedAt?: string;
  /** True when the backend capped this result before it could finish ordering
   *  it, so `rows` is a partial and any sum or count over it understates the
   *  true answer. Independent of totalRowCount, which can be exact and
   *  trustworthy even when rows is a deliberately short top-N slice. */
  truncated?: boolean;
  /** The full row count behind this result when the backend can report it,
   *  whether or not `truncated` is set. */
  totalRowCount?: number;
  /** True when the backend already grouped these rows (a governed dataset's card compiles
   *  straight to one aggregate query). The client must not re-apply an aggregate other than sum
   *  on top of them: one row per group means countDistinct/avg/min/max/first would be wrong. */
  aggregated?: boolean;
  query?: { sql: string; parameters: Record<string, unknown> };
};

/**
 * Every key the Lens spec contract allows, as runtime lists.
 *
 * The same lists live in `src/types/lensContract.json` here and in
 * `src/modules/le-lens/lens-contract.json` in svc-reporting, and each repo has
 * a test asserting its own lists against its copy. Neither repo's CI can
 * import the other, so the snapshot is the only place the two can be compared:
 * a key added on one side alone fails that repo's suite rather than being
 * dropped when a board is saved.
 */
export const LENS_SPEC_KEYS = [
  'version',
  'customView',
  'display',
  'cards',
  'datasets',
  'filters',
  'filterColumns',
  'periodDefault',
  'period',
  'calendar',
  'sections',
  'scope',
] as const;

export const LENS_DISPLAY_KEYS = ['compactLayout', 'showTitle', 'colorTreatment'] as const;

export const LENS_SCOPE_KEYS = ['dataset', 'metricBasis', 'period', 'comparison', 'geography', 'attribution'] as const;

export const LENS_CARD_FIELDS = [
  'id',
  'type',
  'title',
  'description',
  'contract',
  'insightQuestion',
  'insight',
  'filterBindings',
  'config',
  'view',
  'datasetId',
  'size',
  'height',
  'layout',
  'section',
  'period',
  'reading',
  'brief',
  'questions',
  'refusal',
] as const;

export const LENS_CARD_BRIEF_KEYS = ['request', 'source', 'notes'] as const;

export const LENS_CARD_REFUSAL_KEYS = ['at', 'reason'] as const;

export const LENS_INSIGHT_KEYS = ['content', 'refreshedAt', 'scope'] as const;

export const LENS_CONFIG_KEYS = [
  'tableColumns',
  'titleAlignment',
  'titleSize',
  'format',
  'fieldFormats',
  'fieldLabels',
  'fieldValueLabels',
  'sort',
  'categoryOrder',
  'seriesSort',
  'labelFormat',
  'xAxis',
  'yAxis',
  'secondaryYAxis',
  'valueLabels',
  'commentary',
  'textScale',
  'referenceLines',
  'seriesMarks',
  'secondaryAxis',
  'colors',
  'stacked',
  'barOrientation',
  'lineStyle',
  'xColumn',
  'yColumn',
  'series',
  'categoryColumn',
  'valueColumn',
  'summary',
  'sparkline',
] as const;

export const LENS_VIEW_KEYS = [
  'mode',
  'comparison',
  'x',
  'series',
  'value',
  'values',
  'aggregate',
  'ratio',
  'limit',
  'where',
] as const;

export const LENS_DATASET_KEYS = [
  'maxAgeSeconds',
  'deletedAt',
  'id',
  'name',
  'sql',
  'version',
  'scope',
  'runAs',
  'residence',
  'description',
  'contract',
  'fields',
  'dimensions',
  'dimensionLabels',
  'dateFilter',
  'columns',
  'grain',
  'source',
  'dryRun',
  'override',
] as const;

export const LENS_DATASET_SCOPES = ['board', 'shared'] as const;

export const LENS_DATASET_RECORD_KEYS = [
  'deletedAt',
  'version',
  'scope',
  'runAs',
  'residence',
  'description',
  'contract',
] as const;

export const LENS_DATASET_FIELD_KEYS = ['name', 'expression', 'when', 'format', 'label'] as const;

export const LENS_DATASET_FIELD_WHEN = ['row', 'aggregate'] as const;

export const LENS_DATASET_COLUMN_KEYS = [
  'name',
  'type',
  'kind',
  'label',
  'format',
  'description',
  'aggregate',
  'ratio',
  'zone',
] as const;

export const LENS_DATASET_GRAIN_KEYS = ['keys', 'verified'] as const;

export const LENS_DATASET_SOURCE_KEYS = ['table', 'kind', 'catalogueId', 'version', 'authoredBy'] as const;

export const LENS_DATASET_AUTHOR_KEYS = ['actor', 'clientId'] as const;

export const LENS_DATASET_DRY_RUN_KEYS = ['at', 'bytesProcessed', 'referencedTables'] as const;

export const LENS_DATASET_OVERRIDE_KEYS = ['overriddenBy', 'at'] as const;

export const LENS_AGGREGATES = ['sum', 'avg', 'median', 'count', 'countDistinct', 'min', 'max', 'first'] as const;

export const LENS_CARD_SIZES = ['small', 'medium', 'large', 'full'] as const;

export const LENS_CARD_HEIGHTS = ['short', 'standard', 'tall', 'xtall'] as const;

export const LENS_CONTRACTS = ['provisional', 'blocked'] as const;

export const LENS_MARKS = ['line', 'bar', 'area'] as const;

export const LENS_VIEW_MODES = ['grouped', 'points'] as const;

export const LENS_SORT_KEYS = ['none', 'label', 'value'] as const;

export const LENS_SERIES_SORTS = ['none', 'largest', 'smallest', 'alpha'] as const;

export const LENS_TEXT_SCALES = ['small', 'medium', 'large'] as const;

export const LENS_VALUE_FORMATS = ['number', 'currency', 'percent'] as const;

export const LENS_LABEL_KINDS = ['text', 'date', 'time'] as const;

export const LENS_CARD_ALIGNMENTS = ['left', 'center', 'right'] as const;

export const LENS_CARD_TITLE_SIZES = ['small', 'medium', 'large'] as const;

export const LENS_SUMMARY_MODES = ['stats'] as const;

/**
 * Compile-time proof that a key list covers its own type. Wrapped in a tuple so
 * an empty difference is not distributed away into `never`, which would let the
 * check pass for any list at all.
 */
export type CoversType<TType, TKeys extends string> = [Exclude<keyof TType, TKeys>] extends [never]
  ? true
  : { keyListIsMissing: Exclude<keyof TType, TKeys> };

export type CardFieldsCoverType = CoversType<LensCard, (typeof LENS_CARD_FIELDS)[number]>;
export type CardBriefKeysCoverType = CoversType<LensCardBrief, (typeof LENS_CARD_BRIEF_KEYS)[number]>;
export type CardRefusalKeysCoverType = CoversType<LensCardRefusal, (typeof LENS_CARD_REFUSAL_KEYS)[number]>;
export type ViewKeysCoverType = CoversType<LensCardView, (typeof LENS_VIEW_KEYS)[number]>;
export type DatasetKeysCoverType = CoversType<LensDataset, (typeof LENS_DATASET_KEYS)[number]>;
export type DatasetRecordKeysCoverType = CoversType<LensDatasetRecord, (typeof LENS_DATASET_RECORD_KEYS)[number]>;
export type DatasetFieldKeysCoverType = CoversType<LensDatasetField, (typeof LENS_DATASET_FIELD_KEYS)[number]>;
export type DatasetColumnKeysCoverType = CoversType<LensDatasetColumn, (typeof LENS_DATASET_COLUMN_KEYS)[number]>;
export type DatasetGrainKeysCoverType = CoversType<LensDatasetGrain, (typeof LENS_DATASET_GRAIN_KEYS)[number]>;
export type DatasetSourceKeysCoverType = CoversType<LensDatasetSource, (typeof LENS_DATASET_SOURCE_KEYS)[number]>;
export type DatasetAuthorKeysCoverType = CoversType<LensDatasetAuthor, (typeof LENS_DATASET_AUTHOR_KEYS)[number]>;
export type DatasetDryRunKeysCoverType = CoversType<LensDatasetDryRun, (typeof LENS_DATASET_DRY_RUN_KEYS)[number]>;
export type DatasetOverrideKeysCoverType = CoversType<LensDatasetOverride, (typeof LENS_DATASET_OVERRIDE_KEYS)[number]>;
export type ScopeKeysCoverType = CoversType<LensScope, (typeof LENS_SCOPE_KEYS)[number]>;

/**
 * Fails to compile if a list above stops covering its type.
 *
 * LensCardConfig is absent on purpose: it carries an index signature, so every
 * string is already one of its keys and the difference is always empty. Its
 * lockstep is the snapshot test rather than the compiler.
 */
export const LENS_CONTRACT_COVERS_TYPES: [
  CardFieldsCoverType,
  CardBriefKeysCoverType,
  CardRefusalKeysCoverType,
  ViewKeysCoverType,
  DatasetKeysCoverType,
  DatasetFieldKeysCoverType,
  DatasetColumnKeysCoverType,
  DatasetGrainKeysCoverType,
  DatasetSourceKeysCoverType,
  DatasetAuthorKeysCoverType,
  DatasetDryRunKeysCoverType,
  DatasetOverrideKeysCoverType,
  ScopeKeysCoverType,
] = [true, true, true, true, true, true, true, true, true, true, true, true, true];

/**
 * A governed dataset as the catalogue describes it. Served by svc-reporting from a registry the
 * data team maintains (today a checked-in list; later a projection of dbt docs). The server, not
 * the client, owns every table name and expression: the client only ever names fields.
 */
export type LensCatalogueFieldKind = 'date' | 'dimension' | 'measure';

export type LensCatalogueField = {
  name: string;
  label: string;
  kind: LensCatalogueFieldKind;
  /** BigQuery type, e.g. STRING, DATETIME, NUMERIC. */
  type: string;
  description?: string;
  /** Measures only: how the field combines. */
  aggregate?: LensAggregate;
  format?: LensValueFormat;
  /** Measures only: a standard ratio of two other measures, computed as sum(of) / sum(per). */
  ratio?: { of: string; per: string };
  /** The Key Tracking Report field this measure reproduces, when it does. */
  ktrField?: string;
  /** Measures only: which list the picker shows it in. */
  group?: LensCatalogueMeasureGroup;
};

/** How the picker and catalogue group measures: money (currency), counts (sums and distinct counts), rates (ratios and percentages). */
export type LensCatalogueMeasureGroup = 'money' | 'count' | 'rate';

export type LensCatalogueDatasetSummary = {
  topic?: string;
  /** One plain sentence saying what the dataset is for, e.g. "Every sale and refund line, so you can read what was sold and what came back." */
  purpose: string;
  fieldCounts: { dates: number; dimensions: number; measures: number };
  /** Example questions the dataset answers well, in the reader's words. */
  goodFor: Array<string>;
  id: string;
  /** Plain name, e.g. "Sales". */
  name: string;
  /** Fully-qualified table the dataset reads. Shown first in the pane. */
  table: string;
  description: string;
  /** Warehouse layer, e.g. "analytics" or "reporting". */
  layer: string;
  /** How often the table refreshes, in words, e.g. "daily". */
  refresh: string;
  owner?: string;
  /** Registry-authored row-grain identifiers. Omitted when the source contract has no declared grain. */
  grainKeys?: Array<string>;
  /** Analytics domains whose published contract includes this dataset. */
  usedByDomains?: Array<{ id: string; name: string }>;
  status: 'governed';
  version: string;
  fieldCount: number;
  /** Labels of the measures, for the picker card. */
  measureLabels: Array<string>;
};

export type LensCatalogueDataset = LensCatalogueDatasetSummary & {
  fields: Array<LensCatalogueField>;
  /** The column the dashboard period binds to. */
  dateColumn: string;
  /** A ready-to-save dataset (everything but the id) so the client never derives one. */
  draft: Omit<LensDataset, 'id'>;
};

export type LensCatalogueListResponseBody = { datasets: Array<LensCatalogueDatasetSummary> };

/** Compose one card from a request against a governed dataset: no SQL, only fields the catalogue declares. */
export type LensComposeRequestBody = {
  /** The Lens's current governed dataset. Absent when the Lens has none yet. */
  anchor?: LensPlanAnchor;
  /**
   * When true, the server may answer from any governed dataset in the catalogue, not only the
   * anchor: a request contained in one governed table binds to that table, a request that needs
   * a join comes back unanswerable with next 'chat'.
   */
  route?: boolean;
  request: string;
  /** The card being edited, when the request is a change rather than a new card. */
  current?: { type: LensCardType; view?: LensCardView; title?: string };
};

export type LensComposedCard = {
  title: string;
  type: LensCardType;
  view: LensCardView;
  format?: LensValueFormat;
  description?: string;
  /** The governed dataset the card reads. Equal to the anchor's catalogueId unless the server routed elsewhere. */
  catalogueId?: string;
};

export type LensComposeResponseBody =
  | { status: 'composed'; card: LensComposedCard; explanation: string }
  | {
      status: 'unanswerable';
      reason: string;
      missing?: Array<string>;
      /** 'chat' when the question needs a join or a table outside the catalogue, so free SQL in chat is the honest next step. */
      next?: 'chat' | 'none';
      /** Catalogue ids that could answer a related question, for a "try this dataset" hint. */
      alternatives?: Array<string>;
    };

export type LensCatalogueSearchHit = {
  catalogueId: string;
  name: string;
  /** One sentence saying why this dataset matched, naming the fields. */
  why: string;
  /** Field names that matched. */
  fields: Array<string>;
};

export type LensCatalogueSearchResponseBody = {
  query: string;
  hits: Array<LensCatalogueSearchHit>;
  /** One line that separates the hits when more than one dataset answers, or points to chat when none does. */
  note?: string;
};

/**
 * Additive: `week_to_date`, `quarter_to_date`, `year_to_date` and `last_12_months` are new for the
 * Commercial control bar pass (01_IMPLEMENTATION_PLAN.md section 2.1). `last_14_days` stays for
 * backward compatibility with any caller still requesting it; the new ControlBar does not offer it
 * as a preset (00_DESIGN_DIRECTION.md section 3 row 2 lists the presets it does show).
 */
export const ANALYTICS_PERIODS = [
  'yesterday',
  'week_to_date',
  'month_to_date',
  'quarter_to_date',
  'year_to_date',
  'last_7_days',
  'last_14_days',
  'last_28_days',
  'last_90_days',
  'last_12_months',
  'custom',
] as const;
export type AnalyticsPeriod = (typeof ANALYTICS_PERIODS)[number];
export const ANALYTICS_COMPARISONS = ['previous_year', 'previous_period', 'custom', 'none'] as const;
export type AnalyticsComparison = (typeof ANALYTICS_COMPARISONS)[number];
export const ANALYTICS_BREAKDOWNS = [
  'product',
  'experience_provider',
  'accommodation_type',
  'length_of_stay',
  'booking_lead_time',
  'market',
  'sale_channel',
  'platform',
  'customer_type',
  'domestic_international',
  'customer_country',
  'destination_region',
  'margin_max_rebooking',
] as const;
export type AnalyticsBreakdown = (typeof ANALYTICS_BREAKDOWNS)[number];

/** The governed data source backing a Commercial page surface. */
export const ANALYTICS_METRIC_SOURCES = ['sales', 'offer_views'] as const;
export type AnalyticsMetricSource = (typeof ANALYTICS_METRIC_SOURCES)[number];

/** Server-computed trend bucket size. `AnalyticsSnapshotRequest.trendGrain` omits this to ask for the auto rule. */
export const ANALYTICS_TREND_GRAINS = ['day', 'week', 'month'] as const;
export type AnalyticsTrendGrain = (typeof ANALYTICS_TREND_GRAINS)[number];

/**
 * The seven Commercial metric ids the domain snapshot returns (rank order below), fixed by
 * 01_IMPLEMENTATION_PLAN.md section 2.1. `commercial.margin_pct_br` is new this pass; the rest
 * already existed. Kept as a closed union (rather than `AnalyticsMetricDefinition.id: string`)
 * because `src/pages/LeLens/catalogue/commercial/derive.ts` switches on it exhaustively to derive
 * every tile, sparkline and drivers value from the raw trend/breakdown sums.
 */
export const COMMERCIAL_METRIC_IDS = [
  'commercial.ttv_br',
  'commercial.gross_margin_br',
  'commercial.margin_pct_br',
  'commercial.ttv_ar',
  'commercial.gross_margin_ar',
  'commercial.margin_pct_ar',
  'commercial.orders',
  'commercial.aov_br',
  'commercial.aov_ar',
  'commercial.refund_rate',
  'commercial.refunds_pct_gm',
  'commercial.refunds_pct_gm_overrides',
  'commercial.gross_margin_br_overrides',
  'commercial.gross_margin_ar_overrides',
  'commercial.margin_pct_br_overrides',
  'commercial.margin_pct_ar_overrides',
  'commercial.refunds',
  'commercial.room_nights',
  'commercial.cruise_nights',
  'commercial.pax',
  'commercial.total_pax',
  'commercial.packages_attributed',
  'commercial.adr_br',
  'commercial.adr_ar',
  'commercial.avg_package_value_br',
  'commercial.avg_package_value_ar',
  'commercial.gm_per_package_br',
  'commercial.gm_per_package_ar',
  'commercial.ttv_per_pax_br',
  'commercial.ttv_per_pax_ar',
  'commercial.gm_per_pax_br',
  'commercial.gm_per_pax_ar',
  'commercial.ttv_per_cruise_night_br',
  'commercial.ttv_per_cruise_night_ar',
  'commercial.ttv_per_room_night_br',
  'commercial.ttv_per_room_night_ar',
  'commercial.gm_per_room_night_br',
  'commercial.gm_per_room_night_ar',
  'commercial.total_views',
  'commercial.unique_views',
  'commercial.packages',
  'commercial.conversion_rate',
  'commercial.items',
  'commercial.add_to_cart_rate',
  'commercial.revenue_per_view',
  'commercial.revenue_per_view_br',
  'commercial.revenue_per_view_ar',
  'commercial.margin_per_view',
  'commercial.margin_per_view_br',
  'commercial.margin_per_view_ar',
  'commercial.margin_per_view_luxplus',
  'commercial.ttv_br_attributed',
  'commercial.ttv_ar_attributed',
  'commercial.gross_margin_br_attributed',
  'commercial.gross_margin_ar_attributed',
] as const;
export type CommercialMetricId = (typeof COMMERCIAL_METRIC_IDS)[number];

/**
 * The Key Tracking Report's Include Margin Overrides parameter. 'standard' is margin_aud;
 * 'overrides' swaps in the estimated override on sale-side order_item rows, which is what the KTR
 * shows with the toggle on.
 */
export const COMMERCIAL_MARGIN_BASES = ['standard', 'overrides'] as const;
export type CommercialMarginBasis = (typeof COMMERCIAL_MARGIN_BASES)[number];

/**
 * The raw additive sums every derived Commercial figure comes from: a metric value, a share, a
 * trend point, a breakdown row and their comparisons are all one of these four numbers, or a
 * ratio between two of them, computed client-side by `commercial/derive.ts`. The server never
 * pre-divides them, so cumulative and per-bucket ratios stay correct (a ratio's cumulative value
 * is the ratio of the cumulative sums, not the sum of the per-bucket ratios).
 */
export type AnalyticsMeasureSums = {
  gross_sales_aud: number;
  margin_aud: number;
  accounting_refund_amount_aud: number;
  /** Added with the AR contract; absent only while the backend rolls forward. */
  margin_aud_refund?: number;
  /**
   * Margin on the Key Tracking Report's estimated-override basis. Present only when the reader
   * asked for that basis and the governed catalogue declares the measures.
   */
  margin_br_overrides?: number;
  margin_refund_overrides?: number;
  order_bk: number;
  /** Accommodation-only and absent on older reporting responses. */
  room_nights?: number;
  /** Cruise-only volume unit; absent on older reporting responses. */
  cruise_nights?: number;
  /** Passenger count. It is optional while older reporting responses roll forward. */
  total_pax?: number;
  /** Offer Views-only additive inputs. They are absent from Sales responses. */
  view_count?: number;
  unique_view_count?: number;
  packages?: number;
  items?: number;
  add_to_cart_events?: number;
  /** Attributed Offer Views after-refund values; not Sales-after-refund values. */
  ttv_ar?: number;
  gm_ar?: number;
  margin_luxplus_aud?: number;
};

export type AnalyticsFilterOption = {
  id: string;
  label: string;
};

export type AnalyticsSnapshotRequest = {
  period: AnalyticsPeriod;
  from?: string;
  to?: string;
  comparison: AnalyticsComparison;
  comparisonFrom?: string;
  comparisonTo?: string;
  breakdown: AnalyticsBreakdown;
  /** Comma-separated on the wire; unknown ids get a 400 from the server with the allowed list. */
  product?: Array<string>;
  market?: Array<string>;
  saleChannel?: Array<string>;
  /** Canonical Accommodation model ids. Valid only with `product=accommodation`. */
  accommodationModel?: Array<string>;
  /** Accommodation-only KTR slice. Refund rows are not available, so the UI constrains it to BR. */
  marginMaxedOnly?: boolean;
  /** Omitted asks the server to apply the auto grain rule for the resolved period. */
  trendGrain?: AnalyticsTrendGrain;
  /** Sent only when the advertised capability has a non-empty metric list. */
  source?: AnalyticsMetricSource;
  /**
   * Which margin the page reports, mirroring the Key Tracking Report's Include Margin Overrides
   * parameter. Omitted means the standard margin.
   */
  marginBasis?: CommercialMarginBasis;
};

export type AnalyticsBreakdownDefinition = {
  id: AnalyticsBreakdown;
  label: string;
  field: string;
  /** Loyalty only: the programme a breakdown belongs to. */
  programme?: 'societe' | 'luxplus';
};

export type AnalyticsDomainDefinition = {
  id: string;
  version: string;
  name: string;
  description: string;
  state: 'live' | 'planned';
  rank: number;
  datasetIds: Array<string>;
  metricIds: Array<string>;
  coreLensIds: Array<string>;
  periods: Array<{ id: AnalyticsPeriod; label: string }>;
  breakdowns: Array<AnalyticsBreakdownDefinition>;
};

export type AnalyticsMetricDefinition = {
  id: string;
  version: string;
  name: string;
  shortName: string;
  definition: string;
  state: 'draft' | 'certified';
  format: LensValueFormat;
  comparison: 'supported' | 'none';
  /** Which direction of change is good news for this metric. 'down' for refunds and refund rate. */
  favourableDirection: 'up' | 'down';
  /** Fixed display order within the metric strip: ttv_br 1, gross_margin_br 2, margin_pct_br 3, orders 4, aov_br 5, refund_rate 6, refunds 7. */
  rank: number;
  source: {
    catalogueId: string;
    datasetName: string;
    datasetVersion: string;
    measureId: string;
    measure: string;
    /** Validated snapshot scope and native reduction supplied by Reporting. */
    where?: LensCardView['where'];
    view?: Pick<LensCardView, 'value' | 'ratio'>;
  };
};

export type AnalyticsMetricResult = AnalyticsMetricDefinition & {
  status: 'ready' | 'unavailable';
  value?: number;
  comparisonValue?: number;
  absoluteChange?: number;
  percentChange?: number | null;
  unavailableReason?: string;
};

export type AnalyticsBreakdownRow = {
  id: string;
  label: string;
  value: number;
  share: number | null;
  comparisonValue?: number;
  percentChange: number | null;
  /** Raw sums behind this row (TTV basis), so any of the 7 metrics can be derived per row, not just TTV. */
  measures: AnalyticsMeasureSums;
  comparisonMeasures: AnalyticsMeasureSums | null;
};

export type AnalyticsBreakdownResult = {
  /** Raw grouping values and their source-defined display names for faithful Lens transfer. */
  valueLabels?: Record<string, string>;
  id: AnalyticsBreakdown;
  label: string;
  rows: Array<AnalyticsBreakdownRow>;
  /**
   * The selected metric's raw time series for each value in this breakdown. Optional while the
   * reporting service rolls forward; the Commercial page temporarily falls back to its aggregate
   * snapshot trend when it is absent.
   */
  trend?: {
    grain: AnalyticsTrendGrain;
    window: { from: string; to: string; label: string };
    series: Array<{
      id: string;
      label: string;
      points: Array<AnalyticsTrendPoint>;
    }>;
  };
};

/** One trend bucket's raw sums, current and (if the bucket has a comparison counterpart) prior. */
export type AnalyticsTrendPoint = {
  /** Bucket start date, Sydney, YYYY-MM-DD. */
  bucket: string;
  /** Whether this bucket overlaps the selected reporting period (vs. the wider chart window). */
  inPeriod: boolean;
  /** Whether this bucket (the current week/month) is not yet complete. */
  partial: boolean;
  current: AnalyticsMeasureSums;
  /** Null when this bucket has no comparison counterpart (e.g. comparison is 'none'). */
  comparison: AnalyticsMeasureSums | null;
  /** The comparison period's own bucket date, joined by date (not array index). Null alongside `comparison`. */
  comparisonBucket: string | null;
};

/**
 * A compact, reporting-period-exact series for KPI cards. It is deliberately separate from the
 * wider investigation chart context: the backend owns both its inclusive window and any rolling
 * calculation, so the UI never guesses date ranges from the selected preset.
 */
export type AnalyticsSparklineTrend<TPoint> = {
  grain: AnalyticsTrendGrain;
  window: { from: string; to: string; label: string };
  /** Present only when reporting applied a trailing rolling calculation to this series. */
  rollingWindowDays?: number;
  comparisonShift: Exclude<AnalyticsComparison, 'none' | 'custom'> | null;
  points: Array<TPoint>;
};

export type AnalyticsDomainSnapshot = {
  domain: AnalyticsDomainDefinition;
  period: {
    id: AnalyticsPeriod;
    label: string;
    from: string;
    to: string;
    comparison: null | {
      mode: Exclude<AnalyticsComparison, 'none'>;
      label: string;
      from: string;
      to: string;
    };
    timezone: 'Australia/Sydney';
  };
  /** The scope filters applied to every query (summary, breakdown, trend) plus the allowed id/label options. */
  filters: {
    product: Array<AnalyticsFilterOption>;
    market: Array<AnalyticsFilterOption>;
    saleChannel: Array<AnalyticsFilterOption>;
    marginMaxedOnly?: boolean;
    metricCompatibility?: {
      afterRefunds: 'unavailable';
      reason: string;
    };
    applied: boolean;
  };
  metrics: Array<AnalyticsMetricResult>;
  trend: {
    grain: AnalyticsTrendGrain;
    window: { from: string; to: string; label: string };
    comparisonShift: Exclude<AnalyticsComparison, 'none' | 'custom'> | null;
    points: Array<AnalyticsTrendPoint>;
    sparkline?: AnalyticsSparklineTrend<AnalyticsTrendPoint>;
  };
  breakdowns: Array<{
    id: AnalyticsBreakdown;
    label: string;
    rows: Array<AnalyticsBreakdownRow>;
  }>;
  refresh: {
    cadence: string;
    queriedAt: string;
    state: 'fresh' | 'late' | 'failed' | 'observed' | 'not_observed';
    note: string;
    /** BigQuery INFORMATION_SCHEMA.PARTITIONS max(last_modified_time) for the governed table, when observed. */
    lastRefreshedAt: string | null;
    /** Latest Sydney calendar date the source is known to contain complete data for. */
    completedThrough: string;
    /** Latest complete source date; may be later than `period.to` for a historical custom range. */
    dataTo: string;
  };
  source: {
    catalogueId: string;
    datasetName: string;
    datasetVersion: string;
  };
  /**
   * Per-source product contract. Optional while an older reporting service is rolling forward.
   * An available source with an empty metric list is intentionally not queryable.
   */
  sourceCapabilities?: Array<AnalyticsSourceCapability>;
  /** The source that produced this snapshot; absent responses are the original Sales contract. */
  metricSource?: AnalyticsMetricSource;
};

export type AnalyticsSourceCapability = {
  source: AnalyticsMetricSource;
  available: boolean;
  reason?: 'no_validated_product_mapping';
  metricIds: Array<string>;
  breakdowns: Array<AnalyticsBreakdownDefinition>;
};

/** Additive Offer Views measures. Ratios are calculated by the client from these sums. */
export type AnalyticsOfferViewsMeasureSums = {
  view_count: number;
  unique_view_count: number;
  packages: number;
  items: number;
  add_to_cart_events: number;
  gross_sales_aud: number;
  ttv_ar: number;
  gm_br: number;
  gm_ar: number;
  margin_luxplus_aud: number;
};

export type AnalyticsOfferViewsTrendPoint = Omit<AnalyticsTrendPoint, 'current' | 'comparison'> & {
  current: AnalyticsOfferViewsMeasureSums;
  comparison: AnalyticsOfferViewsMeasureSums | null;
};

export type AnalyticsOfferViewsBreakdownRow = Omit<AnalyticsBreakdownRow, 'measures' | 'comparisonMeasures'> & {
  measures: AnalyticsOfferViewsMeasureSums;
  comparisonMeasures: AnalyticsOfferViewsMeasureSums | null;
};

export type AnalyticsOfferViewsBreakdownResult = Omit<AnalyticsBreakdownResult, 'rows' | 'trend'> & {
  rows: Array<AnalyticsOfferViewsBreakdownRow>;
  trend?: {
    grain: AnalyticsTrendGrain;
    window: { from: string; to: string; label: string };
    series: Array<{ id: string; label: string; points: Array<AnalyticsOfferViewsTrendPoint> }>;
  };
};

export type AnalyticsOfferViewsSnapshot = Omit<AnalyticsDomainSnapshot, 'trend' | 'breakdowns' | 'metricSource'> & {
  metricSource: 'offer_views';
  trend: Omit<AnalyticsDomainSnapshot['trend'], 'points' | 'sparkline'> & {
    points: Array<AnalyticsOfferViewsTrendPoint>;
    sparkline?: AnalyticsSparklineTrend<AnalyticsOfferViewsTrendPoint>;
  };
  breakdowns: Array<{ id: AnalyticsBreakdown; label: string; rows: Array<AnalyticsOfferViewsBreakdownRow> }>;
};
